import { useEffect, useRef, useState } from "react";
import CommonModal from "../../../components/shared/modalLayout/CommonModal";
import { Col, Row } from "react-bootstrap";
import SelectBox from "../../../components/shared/fields/SelectBox ";
import InputWithLabel from "../../../components/shared/fields/InputWithLabel";
import Button from "../../../components/shared/buttons/button";
import {
  useCreateRawMaterialMutation,
  useGetFruitsQuery,
  useGetSuppliersQuery,
  useUpdateRawMaterialMutation,
  useGenerateBatchCodeMutation,
} from "./unloadingApi";
import InputWithIcon from "../../../components/shared/fields/InputWithIcon";
import { RefreshSvg } from "../../../svgFiles/RefreshSvg";
import { getCurrentTimeWithSeconds } from "../../../components/shared/utils";
import useTranslator from "../../../hooks/useTranslator";
import { useGetPalletsQuery } from "../../../app/globalApi";
import PalletListDropdown from "../../../components/shared/pallet/PalletListDropdown";
import {
  PALLET_GREEN_CASES,
  EMPTY_GREEN_CASE_WEIGHT,
  EMPTY_PALLET_WEIGHT,
  GREEN_CASE_FRUIT_WEIGHT,
  UNLOADING_PALLETIZE_PALLET_MIN_WEIGHT,
  PLASTIC_EMPTY_PALLET_WEIGHT,
} from "../../../helpers/constants";
import toast from "react-hot-toast";
import moment from "moment";
import { ceilToTwoDecimal, convertToUserTimezone, getCurrentDateTime, minWeightForCases } from "../../../helpers/Utils";

let interval = null;
// const today = moment().format("YYYY-MM-DDTHH:mm");

const PALLET_TOTAL_MIN_WEIGHT =
  PALLET_GREEN_CASES * EMPTY_GREEN_CASE_WEIGHT +
  EMPTY_PALLET_WEIGHT +
  PALLET_GREEN_CASES * GREEN_CASE_FRUIT_WEIGHT;

const PALLET_TOTAL_MAX_WEIGHT =
  PALLET_GREEN_CASES * EMPTY_GREEN_CASE_WEIGHT +
  EMPTY_PALLET_WEIGHT +
  PALLET_GREEN_CASES * GREEN_CASE_FRUIT_WEIGHT +
  120;

const UnloadingModal = ({ show, setShow, unloading, mode }) => {
  const [currentDateTime, setCurrentDateTime] = useState(getCurrentDateTime("YYYY-MM-DDTHH:mm"));


  // Initial Data
  const initialFormData = {
    supplier_id: "",
    fruit_type_id: "",
    unloading_date : currentDateTime,
    batch_code: "",
    time: getCurrentTimeWithSeconds(),
    brix_level: "",
    receiving_pallets: [],
    palletize_cases: 0,
    non_palletize_cases: 0,
    kg_entered: "",
    total_cases: 0,
    total_pallets: 0,
    status: "",
    case_fresh: "28",
  };

  // RTK Query
  const [createRawMaterial, { isLoading: isCreating }] =
    useCreateRawMaterialMutation();
  const [updateRawMaterial, { isLoading: isUpdating }] =
    useUpdateRawMaterialMutation();
  const [generateBatchCodeApi] = useGenerateBatchCodeMutation();
  const { data: supplierList } = useGetSuppliersQuery({ role_id: "raw_material_supplier" });
  const { data: fruitList } = useGetFruitsQuery();
  const { data: pallets, refetch: refetchPallets } = useGetPalletsQuery();

  // Ref
  const skipReceivingPalletsEffect = useRef(false);

  // States
  const [selectedPalletId, setSelectedPalletId] = useState("");
  const [selectedPalletTotalKg, setSelectedPalletTotalKg] = useState("");
  const [selectedPalletTotalCases, setSelectedPalletTotalCases] = useState("");
  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [nonPalletizedPallet, setNonPalletizedPallet] = useState(null);
  const [localPallets, setLocalPallets] = useState([]);
  const [palletTotalMinWeight, setPalletTotalMinWeight] = useState(0);
  const [palletTotalMaxWeight, setPalletTotalMaxWeight] = useState(0);

  // Translation
  const { _t } = useTranslator();
  const messages = {
    selectPallet: _t("please_select_a_pallet", "Please select a pallet"),
    validKg: _t(
      "please_enter_a_valid_pallet_kg",
      "Please enter a valid pallet KG"
    ),
    validCases: _t(
      "please_enter_valid_pallet_cases",
      "Please enter valid pallet cases (1–30)"
    ),
    onlyOneLowWeight: _t(
      "only_one_pallet_with_weight_less_than",
      "Only one pallet with weight less than"
    ),
    isAllowed: _t("is_allowed", "is allowed"),
    deleteExisting: _t(
      "please_delete_the_existing_one_first",
      "Please delete the existing one first"
    ),
    kgBetween: _t("pallet_kg_must_be_between", "Pallet KG must be between"),
    and: _t("and", "and"),
    kgTooLow: _t(
      "pallet_kg_entered_too_low",
      "Pallet KG entered is too low for the number of cases"
    ),
    negativeValue: _t("value_cannot_be_negative", "Value cannot be negative"),
    invalidNumber: _t(
      "please_enter_valid_number",
      "Please enter a valid number"
    ),
    kgLessOrEqual: _t(
      "pallet_kg_entered_must_be_equal_or_less",
      "Pallet KG entered is must be equal or less than"
    ),
  };

  // Effects

  useEffect(() => {
    const PALLET_TOTAL_MIN_WEIGHT =
      PALLET_GREEN_CASES * EMPTY_GREEN_CASE_WEIGHT +
      EMPTY_PALLET_WEIGHT +
      PALLET_GREEN_CASES * parseInt(formData?.case_fresh);

    const PALLET_TOTAL_MAX_WEIGHT =
      PALLET_GREEN_CASES * EMPTY_GREEN_CASE_WEIGHT +
      EMPTY_PALLET_WEIGHT +
      PALLET_GREEN_CASES * parseInt(formData?.case_fresh) +
      190; // 190kg max weight for the pallet

    setPalletTotalMinWeight(PALLET_TOTAL_MIN_WEIGHT);
    setPalletTotalMaxWeight(PALLET_TOTAL_MAX_WEIGHT);
  }, [formData.case_fresh]);

  useEffect(() => {
    if (pallets) {
      setLocalPallets([...pallets?.data]);
    }
  }, [pallets]);

  useEffect(() => {
    // console.log(mode);
    if (mode === "edit" && unloading) {
      const updatedFormData = { ...initialFormData };
      for (const key in unloading) {
        if (key in initialFormData) {
          // Check if the key exists in formData
          // updatedFormData[key] = unloading[key];
          if (key === 'unloading_date' && unloading[key]) {
            updatedFormData[key] = convertToUserTimezone(unloading[key], "YYYY-MM-DDTHH:mm");
          } else if (key === 'case_fresh') {
            updatedFormData[key] = unloading[key] ? String(unloading[key]) : "28";
          } else {
            updatedFormData[key] = unloading[key];
          }
        }

      }

      setFormData(updatedFormData);

      let nonPalletizedPalletFiltered = updatedFormData?.receiving_pallets.find(
        (p) => p.pallet_type === "non_palletize"
      );
      if (nonPalletizedPalletFiltered) {
        setNonPalletizedPallet(nonPalletizedPalletFiltered);
      }
    } else {
        const freshDateTime = getCurrentDateTime("YYYY-MM-DDTHH:mm");
        setCurrentDateTime(freshDateTime);
        
        const freshFormData = {
          ...initialFormData,
          unloading_date: freshDateTime,
        };
        
        setFormData(freshFormData);
    }
  }, [mode, unloading, show]);

  useEffect(() => {
    if (skipReceivingPalletsEffect.current) {
      skipReceivingPalletsEffect.current = false;
      return;
    }
    const totalPallets = getTotalPallets(formData.receiving_pallets);
    const totalKg = getTotalPalletKg(formData.receiving_pallets);

    const totalCases = calculateTotalCasesFromPallets(
      formData.receiving_pallets
    );

    const totalPalletizeCases =
      Math.floor(totalCases / PALLET_GREEN_CASES) * PALLET_GREEN_CASES;
    const totalNotPalletizeCases = totalCases % PALLET_GREEN_CASES;

    setFormData((prev) => ({
      ...prev,
      total_pallets: totalPallets,
      kg_entered: totalKg,
      palletize_cases: totalPalletizeCases,
      non_palletize_cases: totalNotPalletizeCases,
      total_cases: totalCases,
    }));
  }, [formData.receiving_pallets]);

  useEffect(() => {
    if (
      nonPalletizedPallet &&
      formData?.case_fresh == GREEN_CASE_FRUIT_WEIGHT
    ) {
      let non_palletize_cases = formData.non_palletize_cases || 0;
      if (non_palletize_cases >= 0) {
        const updatedPallets = formData.receiving_pallets.map((p) => {
          if (p.pallet_type === "non_palletize") {
            return {
              ...p,
              pallet_total_cases: parseInt(non_palletize_cases),
            };
          }
          return p;
        });

        skipReceivingPalletsEffect.current = true;

        const palletizeCasesFromPallets =
          calculatePalletizeCasesFromPallets(updatedPallets);
        const nonPalletizeCasesFromPallets =
          calculateNonPalletizeCasesFromPallets(updatedPallets);
        const totalCases =
          palletizeCasesFromPallets + nonPalletizeCasesFromPallets;

        setFormData((prev) => ({
          ...prev,
          receiving_pallets: updatedPallets,
          total_cases: totalCases,
        }));
      }
    }
  }, [formData.non_palletize_cases]);

  useEffect(() => {
    const fetchBatchCode = async () => {
      if (formData?.supplier_id && supplierList?.data) {
        // Supplier name
        const supplierName = supplierList?.data.find(
          (supplier) => supplier.id == formData?.supplier_id
        )?.name;

        if (supplierName) {
          try {
            // Call API to generate batch code
            const response = await generateBatchCodeApi({
              supplier_id: formData.supplier_id,
              supplierName: supplierName,
              unloading_date: formData.unloading_date,
              time: formData.time,
            }).unwrap();

            // Update form data with batch code from API
            setFormData((prevFormData) => ({
              ...prevFormData,
              batch_code: response?.data?.batch_code || "",
            }));
          } catch (error) {
            console.error("Error generating batch code:", error);
            // Fallback to local generation if API fails
            const newBatchCode = generateBatchCode(
              supplierName,
              formData.unloading_date,
              formData.time
            );
            setFormData((prevFormData) => ({
              ...prevFormData,
              batch_code: newBatchCode,
            }));
          }
        }
      }
    };

    if(mode == 'add'){
      fetchBatchCode();
    }
  }, [formData?.supplier_id, formData.time, formData.unloading_date ,mode]);

  useEffect(() => {
    // console.log(` Handling iin this use effect : selectedPalletTotalKg :  ${selectedPalletTotalKg}`);
    
    const handler = setTimeout(() => {
      const kg = parseFloat(selectedPalletTotalKg);
      if (selectedPalletTotalKg && !isNaN(kg)) {
        if (kg >= palletTotalMinWeight && kg <= palletTotalMaxWeight) {
          setSelectedPalletTotalCases(PALLET_GREEN_CASES);
        } else {
          setSelectedPalletTotalCases("");
        }
      } else {
        setSelectedPalletTotalCases("");
      }
    }, 300);

    return () => clearTimeout(handler);
  }, [selectedPalletTotalKg, palletTotalMinWeight]);

  // Functions
  const getTotalPallets = (receiving_pallets = []) => {
    return receiving_pallets.length || 0;
  };

  const getTotalPalletKg = (receiving_pallets = []) => {
    const total = receiving_pallets.reduce(
      (sum, p) => sum + (p.pallet_kg_entered || 0),
      0
    );
    return Math.round(total * 1000) / 1000;
  };

  const calculateTotalCasesFromPallets = (receiving_pallets = []) => {
    return receiving_pallets.reduce(
      (sum, p) => sum + (p.pallet_total_cases || 0),
      0
    );
  };

  const calculatePalletizeCasesFromPallets = (receiving_pallets = []) => {
    const validPallets = receiving_pallets.filter(
      (p) => p.pallet_type === "palletize"
    );
    return validPallets.reduce(
      (sum, p) => sum + (p.pallet_total_cases || 0),
      0
    );
  };

  const calculateNonPalletizeCasesFromPallets = (receiving_pallets = []) => {
    const validPallets = receiving_pallets.filter(
      (p) => p.pallet_type === "non_palletize"
    );
    return validPallets.reduce(
      (sum, p) => sum + (p.pallet_total_cases || 0),
      0
    );
  };

  const getRawMaterialLabel = (mode) => {
    switch (mode) {
      case "add":
        return _t("add_raw_material", "Add Raw Material");
      case "edit":
        return _t("edit_raw_material", "Edit Raw Material");
      default:
        return _t("raw_material", "Raw Material");
    }
  };


  const generateBatchCode = (supplierName, date, time) => {
    const dateObj = date ? new Date(date) : new Date();
    
    // Get month abbreviation (e.g., "Jul")
    const monthAbbr = dateObj.toLocaleString("en-US", { month: "short" });
    
    // Get day (e.g., "18")
    const day = String(dateObj.getDate());
    
    // Parse supplier name
    const nameParts = supplierName.trim().split(" ");
    
    // Get first letter of first name (e.g., "E" from "Everardo")
    const firstNameInitial = nameParts[0] ? nameParts[0].charAt(0).toUpperCase() : "";
    
    // Get last name (e.g., "Regalado")
    const lastName = nameParts.length > 1 ? nameParts[nameParts.length - 1] : "";
    
    // Generate 4-digit random number
    const randomNumber = Math.floor(1000 + Math.random() * 9000); // Generates number between 1000-9999
    
    // Combine all parts: Jul18ERegalado1234
    return `${monthAbbr}${day}${firstNameInitial}${lastName}${randomNumber}`;
  };

  // Handlers
  const handleClose = () => {
    setShow(false);
  };

  const handleSubmit = async (e) => {
    // prevent double submission
    if (isCreating || isUpdating) return;

    if (
      nonPalletizedPallet &&
      formData?.case_fresh == GREEN_CASE_FRUIT_WEIGHT
    ) {
      let max_non_palletize_cases = parseInt(
        nonPalletizedPallet?.pallet_total_cases
      );
      let min_non_palletize_cases =
        max_non_palletize_cases > 0 ? max_non_palletize_cases - 1 : 0;
      let non_palletize_cases = parseInt(formData?.non_palletize_cases);

      if (
        non_palletize_cases < min_non_palletize_cases ||
        non_palletize_cases > max_non_palletize_cases
      ) {
        toast.error(
          `${_t(
            "non_palletized_cases_must_be_between",
            "Non Palletized Cases must be between"
          )} ${min_non_palletize_cases} ${_t(
            "and",
            "and"
          )} ${max_non_palletize_cases}.`
        );
        return;
      }
    }
    const mutation = mode === "add" ? createRawMaterial : updateRawMaterial;

    try {
      const payload =
        mode === "add"
          ? { ...formData }
          : { id: unloading.id, requestBody: { ...formData } };

      await mutation(payload).unwrap();
      refetchPallets();
      handleClose();
    } catch (error) {
      console.error("Error creating/updating manager:", error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const numericValue = Number(value);
    if (name === "case_fresh") {
      if (formData?.receiving_pallets?.length > 0) {
        return false;
      }
    }

    if (
      name === "non_palletize_cases" &&
      formData?.case_fresh != GREEN_CASE_FRUIT_WEIGHT
    ) {
      return false;
    }

    // Validation: Prevent negative values
    if (numericValue < 0) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [name]: "Value cannot be negative",
      }));
      return;
    }

    // Clear error when input is valid
    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: "",
    }));

    setFormData((prevFormData) => ({
      ...prevFormData,
      [name]: value,
    }));
  };

  const calculateNewPallet = (kg, cases) => {
    const netKg = kg - (cases * EMPTY_GREEN_CASE_WEIGHT + EMPTY_PALLET_WEIGHT);
   
    if (netKg < 0) return null;

    const isPalletized = netKg >= 30 * GREEN_CASE_FRUIT_WEIGHT;

    // Always calculate cases based on 28kg
    let approxCases = cases;
    if (formData?.case_fresh != GREEN_CASE_FRUIT_WEIGHT) {
      const rawValue = netKg / GREEN_CASE_FRUIT_WEIGHT;
      approxCases =
        rawValue % 1 >= 0.5 ? Math.ceil(rawValue) : Math.floor(rawValue);
    }

    return {
      // pallet_kg_entered: netKg.toFixed(2),
      pallet_kg_entered: netKg,
      pallet_total_cases:
        isPalletized && formData.case_fresh == GREEN_CASE_FRUIT_WEIGHT
          ? PALLET_GREEN_CASES
          : approxCases,
      pallet_type: isPalletized ? "palletize" : "non_palletize",
    };

  };

  const addPalletHandler = () => {
    if (!selectedPalletId) {
      toast.error(messages.selectPallet);
      return;
    }

    const kg = parseFloat(selectedPalletTotalKg);
    if (isNaN(kg)) {
      toast.error(messages.validKg);
      return;
    }

    const cases = parseInt(selectedPalletTotalCases);
    if (isNaN(cases) || cases <= 0 || cases > PALLET_GREEN_CASES) {
      toast.error(messages.validCases);
      return;
    }

    const calculated_min_weight = (
        (selectedPalletTotalCases * EMPTY_GREEN_CASE_WEIGHT + PLASTIC_EMPTY_PALLET_WEIGHT) +
        (selectedPalletTotalCases * GREEN_CASE_FRUIT_WEIGHT)
    ).toFixed(1);

    if(Number(selectedPalletTotalKg) < calculated_min_weight ){
      toast.error(_t(
        "min_weight_should_be_for_cases",
        "Minimum weight should be :calculated_min_weight kg for :selected_cases cases",
        { calculated_min_weight:calculated_min_weight, selected_cases: selectedPalletTotalCases }
      ));
      return;
    }
    
 // validate weight against cases
    const minExpectedKg = ceilToTwoDecimal(
      cases * parseInt(formData?.case_fresh) +
        cases * EMPTY_GREEN_CASE_WEIGHT +
        EMPTY_PALLET_WEIGHT
    );

    const maxExpectedKg = ceilToTwoDecimal(minExpectedKg + 190);

    // Weight-related validations
    if (kg < palletTotalMinWeight && kg > 0) {
      const hasNonPalletizeCase = formData?.receiving_pallets?.some(
        (p) => parseFloat(p.pallet_kg_entered) < 30 * formData?.case_fresh
      );
      if (hasNonPalletizeCase) {
        toast.error(
          `${messages.onlyOneLowWeight} ${palletTotalMinWeight} ${messages.isAllowed}. ${messages.deleteExisting}`
        );
        return;
      }
    } else if (kg < palletTotalMinWeight || kg > palletTotalMaxWeight) {
      toast.error(
        _t("pallet_kg_must_be_between", "Pallet KG must be between :min_expected_kg kg and :max_expected_kg kg.", {
          min_expected_kg: minExpectedKg,
          max_expected_kg: maxExpectedKg,
        })
      );
      return;
    }

   

    if (cases == PALLET_GREEN_CASES || kg >= palletTotalMinWeight) {
      if (kg < minExpectedKg || kg > maxExpectedKg) {
        toast.error(
          _t("pallet_kg_must_be_between", "Pallet KG must be between :min_expected_kg kg and :max_expected_kg kg.", {
            min_expected_kg: minExpectedKg,
            max_expected_kg: maxExpectedKg,
          })
        );
        return;
      }
    }

    const newPallet = calculateNewPallet(kg, cases);


    if (!newPallet) {
      toast.error(messages.kgTooLow);
      return;
    }

    newPallet.pallet_id = selectedPalletId;

    if (newPallet.pallet_type === "non_palletize") {
      setNonPalletizedPallet(newPallet);
    }

    setFormData((prev) => ({
      ...prev,
      receiving_pallets: [...prev.receiving_pallets, newPallet],
    }));

    setSelectedPalletId("");
    setSelectedPalletTotalKg("");
    setSelectedPalletTotalCases("");
  };

  const isPalletAvailable = (pallet) => {
    const alreadySelected = (formData.receiving_pallets || []).some(
      (p) => p.pallet_id == pallet.id
    );

    return !alreadySelected;
  };

  const deleteHandler = (indexToRemove) => {
    const removePallet = formData?.receiving_pallets[indexToRemove];
    if (removePallet && removePallet?.pallet_type === "non_palletize") {
      setNonPalletizedPallet(null);
    }
    setFormData((prev) => ({
      ...prev,
      receiving_pallets: prev.receiving_pallets.filter(
        (_, i) => i !== indexToRemove
      ),
    }));

    if (removePallet) {
      const updatedLocalPallets = localPallets?.map((localPallet) => {
        if (localPallet.id == removePallet.pallet_id) {
          return { ...localPallet, pallet_status: "available" };
        }
        return { ...localPallet };
      });
      setLocalPallets(updatedLocalPallets);
    }
  };

  const canEditNonPalletizedCases =
    !!nonPalletizedPallet && formData?.case_fresh == GREEN_CASE_FRUIT_WEIGHT;

  const getNonPalletizedCaseOptions = () => {
    const currentValue = Number(formData?.non_palletize_cases || 0);

    if (!canEditNonPalletizedCases) {
      return [{ value: currentValue, label: String(currentValue) }];
    }

    const maxCases = Number(nonPalletizedPallet?.pallet_total_cases || 0);
    const minCases = maxCases > 0 ? maxCases - 1 : 0;

    return [...new Set([minCases, maxCases])]
      .sort((a, b) => a - b)
      .map((caseCount) => ({
        value: caseCount,
        label: String(caseCount),
      }));
  };

  const renderFormFields = () => (
    <>
      <Col lg={6}>
        <SelectBox
          label={_t("supplier_name", "Supplier Name")}
          type="text"
          name="supplier_id"
          value={formData.supplier_id}
          options={supplierList?.data.map((list) => ({
            value: list.id,
            label: list.name,
          }))}
          onChange={handleChange}
          required
        />
      </Col>
      <Col lg={6}>
        <SelectBox
          label={_t("fruit_type", "Fruit Type")}
          name="fruit_type_id"
          options={fruitList?.data.map((list) => ({
            value: list.id,
            label: list.name,
          }))}
          value={formData.fruit_type_id}
          required
          onChange={handleChange}
        />
      </Col>
      <Col lg={6}>
        <InputWithLabel
          label={_t("batch_code", "Batch Code")}
          type="text"
          name="batch_code"
          value={formData.batch_code}
          disabled={true}
          placeholder={_t("batch_code", "Batch Code")}
          required
        />
      </Col>
      <Col lg={6}>
        <Row>
          <Col ld={6}>
            <InputWithLabel
              label={_t("unloading_date", "Unloading Date")}
              type="datetime-local"
              name="unloading_date"
              value={formData.unloading_date}
              onChange={handleChange}
              required
              max={currentDateTime}
              disabled={true}
            />
          </Col>
        </Row>
      </Col>
      <Col lg={6}>
        <InputWithLabel
          label={_t("brix_level", "Brix Level")}
          type="number"
          name="brix_level"
          value={formData.brix_level}
          onChange={handleChange}
          placeholder={"0"}
          error={errors.brix_level}
          required
        />
      </Col>
      <Col lg={6}>
        <SelectBox
          label={_t("case_weight", "Case Weight")}
          name="case_fresh"
          value={formData.case_fresh}
          options={Array.from({ length: 9 }, (_, i) => ({
            value: (28 - i).toString(),
            label: (28 - i).toString(),
          }))}
          disabled={formData?.receiving_pallets?.length > 0}
          onChange={handleChange}
        />
      </Col>
      <Col lg={12}>
        <div className="d-flex gap-2 align-items-center">
          <SelectBox
            label={_t("select_pallet", "Select Pallet")}
            name="pallet_id"
            options={localPallets.map((list) => ({
              value: list.id,
              label: list.pallet_name,
              disabled: !isPalletAvailable(list),
            }))}
            value={selectedPalletId}
            required={false}
            onChange={(e) => {
              setSelectedPalletId(e.target.value);
            }}
            className="w-100"
          />
          <div className="cmn_group_height w-100">
            <InputWithIcon
              label={_t("total_pallet_kg", "Total Pallet KG")}
              type="number"
              name="total_pallet_kg"
              icon={RefreshSvg}
              value={selectedPalletTotalKg}
              onChange={(e) => {
                setSelectedPalletTotalKg(e.target.value);
              }}
              placeholder={"0"}
            />
          </div>
          <div className="cmn_group_height w-100">
            <SelectBox
              label={_t("total_pallet_cases", "Total Pallet Cases")}
              name="total_pallet_cases"
              value={selectedPalletTotalCases}
              options={Array.from({ length: 30 }, (_, i) => ({
                value: (30 - i).toString(),
                label: (30 - i).toString(),
              }))}
              onChange={(e) => setSelectedPalletTotalCases(e.target.value)}
            />
          </div>
          <div className="mt-1">
            <Button
              label={_t("add", "Add")}
              size="small"
              onClick={addPalletHandler}
              isLoading={isCreating || isUpdating}
              className={"addPallets"}
            />
          </div>
        </div>
      </Col>
      <Col lg={6}>
        <PalletListDropdown
          pallets={formData.receiving_pallets || []}
          onDelete={deleteHandler}
        />
      </Col>
      <Col lg={6}>
        <div className="d-flex gap-2">
          <div className="w-100">
            <InputWithIcon
              label={`${_t("palletized_cases", "Palletized Cases")} (per 28)`}
              type="text"
              name="palletize_cases"
              value={formData.palletize_cases}
              disabled={true}
            />
          </div>
          <div className="w-100">
            <SelectBox
              label={`${_t(
                "not_palletized_cases",
                "Not Palletized Cases"
              )} (per 28)`}
              name="non_palletize_cases"
              value={Number(formData?.non_palletize_cases || 0)}
              options={getNonPalletizedCaseOptions()}
              disabled={!canEditNonPalletizedCases}
              onChange={handleChange}
            />
          </div>
        </div>
      </Col>
      <Col lg={6}>
        <InputWithIcon
          label={_t("total_kg_entered", "Total KG Entered")}
          type="text"
          name="kg_entered"
          value={formData.kg_entered ?? 0}
          disabled={true}
        />
      </Col>

      <Col lg={6}>
        <InputWithIcon
          label={`${_t("total_cases_entered", "Total Cases Entered")} (per 28)`}
          type="text"
          name="total_cases"
          value={formData.total_cases}
          disabled={true}
        />
      </Col>
      <Col lg={6}>
        <SelectBox
          label={_t("status", "Status")}
          type="text"
          name="status"
          value={formData.status}
          options={[
            {
              value: "counting",
              label: _t("still_counting", "Still Counting"),
            },
            {
              value: "washing_ready",
              label: _t("ready_for_washing", "Ready for Washing"),
            },
          ]}
          onChange={handleChange}
        />
      </Col>
    </>
  );

  return (
    <CommonModal
      show={show}
      handleClose={handleClose}
      setShow={setShow}
      title={getRawMaterialLabel(mode)}
      children={formData}
    >
      <Row>{renderFormFields()}</Row>
      <Row>
        <Col className="text-end">
          <Button
            label={
              mode === "add"
                ? _t("add_raw_material", "Add Raw Material")
                : _t("save_changes", "Save Changes")
            }
            size="small"
            onClick={handleSubmit}
            disabled={isCreating || isUpdating}
          />
        </Col>
      </Row>
    </CommonModal>
  );
};

export default UnloadingModal;
